import React, { createContext, useContext, useReducer } from 'react'
import {
	type OnboardedBuilderNetworkResponse,
	GetSelectionListByNameResult,
} from '@adsk/napi-api-client'
import {
	type OnboardingUserAppState,
	type OnboardingUserAppContextType,
	type OnboardingUserAppAction,
	type OnboardingUserAppErrorType,
	type OnboardingUserAppProviderProps,
	type FileInfoType,
	ActionStates,
	SupportedProfilePictureAction,
} from '../types/ActionTypes'
import { type Config } from '@adsk/napi-config'

const initialState: OnboardingUserAppState = {
	userInfo: {} as OnboardedBuilderNetworkResponse & {
		id: string
	},
	immutableUserInfo: {} as OnboardedBuilderNetworkResponse & {
		id: string
	},
	userInfoChanged: false,
	fileInfo: undefined,
	profileReadOnlyMode: true,
	displaySubmitButtonLoader: false, // display loader on submit button
	submitState: false, // state of the submit button, whether or not submit button is enabled or not
	showLoader: true, // whenever any api is happening, this will be true and shows up as an overlay on the modal / page
	config: {},
	selectionsList: [],
	marketingCompanyType: {},
}

const appReducer = (
	state: OnboardingUserAppState,
	action: OnboardingUserAppAction
): OnboardingUserAppState => {
	switch (action.type) {
		case ActionStates.SET_FLOW_TYPE:
			return {
				...state,
				flowType: action.payload as unknown as string,
			}
		case ActionStates.SET_SELECTIONS_LIST:
			return {
				...state,
				selectionsList: action.payload as GetSelectionListByNameResult[],
			}
		case ActionStates.SET_IMMUTABLE_USER_INFO:
			return {
				...state,
				immutableUserInfo: {
					...state.userInfo,
					...(action.payload as Partial<OnboardedBuilderNetworkResponse>),
				},
			}
		case ActionStates.SET_USER_INFO_CHANGED:
			return {
				...state,
				userInfoChanged: action.payload as boolean,
			}
		case ActionStates.SET_USER_INFO:
			return {
				...state,
				userInfo: {
					...state.userInfo,
					...(action.payload as Partial<OnboardedBuilderNetworkResponse>),
				},
			}
		case ActionStates.TOGGLE_PROFILE_READ_ONLY_MODE:
			return {
				...state,
				profileReadOnlyMode: !state.profileReadOnlyMode,
			}
		case ActionStates.DISPLAY_SUBMIT_BUTTON_LOADER:
			return {
				...state,
				displaySubmitButtonLoader: action.payload as boolean,
			}
		case ActionStates.SUBMIT_STATE:
			return {
				...state,
				submitState: action.payload as boolean,
			}
		case ActionStates.SHOW_LOADER:
			return {
				...state,
				showLoader: action.payload as boolean,
			}
		case ActionStates.UPDATE_CROPPED_STATE:
			const actionPayload = action.payload as Partial<FileInfoType>
			return {
				...state,
				fileInfo: {
					...state.fileInfo,
					imageRefElement: actionPayload!.imageRefElement as HTMLImageElement,
					cropProps: {
						canvasRef: actionPayload!.cropProps?.canvasRef as HTMLCanvasElement,
						pixelCrop: actionPayload!.cropProps?.pixelCrop,
					},
				} as FileInfoType,
			}
		case ActionStates.UPDATE_AVATAR_FILE_STATE:
			return {
				...state,
				fileInfo: action.payload as FileInfoType,
			}
		case ActionStates.RESET_AVATAR_CURRENT_SELECTION_STATE:
			return {
				...state,
				fileInfo: undefined,
			}
		case ActionStates.SET_ERROR_INFO:
			const errorPayload = action.payload as OnboardingUserAppErrorType
			return {
				...state,
				errorInfo: errorPayload,
			}
		case ActionStates.UPDATE_APP_CONFIG:
			return {
				...state,
				config: action.payload as Config,
			}
		case ActionStates.RESET_STATE:
			return {
				...state,
				submitState: false,
				showLoader: false,
				errorInfo: undefined,
			}
		case ActionStates.SET_PROFILE_PICTURE_ACTION:
			return {
				...state,
				fileInfo: {
					...state.fileInfo,
					action: action.payload as SupportedProfilePictureAction,
				} as FileInfoType,
			}
		case ActionStates.SET_MARKETING_COMPANY_TYPE:
			return {
				...state,
				marketingCompanyType: {
					...state.marketingCompanyType,
					...(action.payload as Record<string, boolean>),
				},
			}
		default:
			return state
	}
}

export const OnboardingUserAppContext = createContext<
	OnboardingUserAppContextType | undefined
>(undefined)

export const useOnboardingUserContext = ():
	| OnboardingUserAppContextType
	| undefined => {
	const context = useContext(OnboardingUserAppContext)
	if (context === null) {
		throw new Error(
			'OnboardingUserAppContext must be used within a OnboardingUserAppProvider'
		)
	}
	return context
}

export const OnboardingUserAppProvider: React.FC<
	OnboardingUserAppProviderProps
> = ({ children, customInitialState, internalProps, externalProps }) => {
	const [state, dispatch] = useReducer(
		appReducer,
		customInitialState ?? initialState
	)
	return (
		<OnboardingUserAppContext.Provider
			value={{ state, dispatch, internalProps, externalProps }}
		>
			{children}
		</OnboardingUserAppContext.Provider>
	)
}
